import styled from 'styled-components';
import { useUser } from '../../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import QuotaService from '../../../service/quota/Quota';

type MembershipLabel = 'Free' | 'Promotion' | 'Standard' | 'Premium';

export interface CountItem {
  type: string;
  count: number;
  limit: number;
}

function RemainingTicket() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [userCount, setUserCount] = useState<CountItem[]>([]);

  const membershipLabelMap: Record<string, MembershipLabel> = {
    P: 'Premium',
    S: 'Standard',
    T: 'Promotion',
  };

  const label: MembershipLabel = user?.plan_grade
    ? (membershipLabelMap[user.plan_grade] ?? 'Free')
    : 'Free';

  const getCount = async () => {
    try {
      const res = await QuotaService.count();
      setUserCount(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCount();
  }, []);

  const getValue = (code: string) => {
    const f = userCount.find((x) => x.type === code);
    return { count: f?.count ?? 0, limit: f?.limit ?? 0 };
  };

  const membershipTicketCount = useMemo(
    () => [
      {
        title: '경공매 상세정보 열람',
        desc: '경공매 검색',
        ...getValue('A'),
      },
      {
        title: 'AI 경공매 찾기',
        desc: 'AI 땅집봇',
        ...getValue('chat'),
      },
      {
        title: '리포트 열람권',
        desc: 'VIP 퀀트 분석 리포트',
        ...getValue('B'),
      },
      {
        title: 'AI 경매 알고리즘 만들기',
        desc: 'AI 경매 알고리즘',
        ...getValue('D'),
      },
      {
        title: 'AI 경매 알고리즘 수익분석',
        desc: 'AI 경매 알고리즘',
        ...getValue('I'),
      },
      {
        title: 'AI 경매 알고리즘 추천물건',
        desc: 'AI 경매 알고리즘',
        ...getValue('E'),
      },
      {
        title: '등기부등본 열람',
        desc: '등기부/전입세대열람원',
        ...getValue('G'),
      },
      {
        title: '전입세대열람원(아파트) 열람',
        desc: '등기부/전입세대열람원',
        ...getValue('H'),
      },
      {
        title: '부실채권(NPL) 투자수익분석',
        desc: '부실채권(NPL)',
        ...getValue('C'),
      },
      {
        title: '빌딩, 농지 심화분석',
        desc: '플러스분석',
        ...getValue('F'),
      },
    ],

    [userCount],
  );

  const fmt = (n: number) => n.toLocaleString('ko-KR');
  return (
    <Container>
      <Title>잔여 이용권</Title>
      {label === 'Free' ? (
        <EmptySection>
          <EmptyDesc>보유 중인 이용권이 없습니다.</EmptyDesc>
          <BuyButton onClick={() => navigate('/payment/plan')}>
            이용권 구매
          </BuyButton>
        </EmptySection>
      ) : (
        <Content>
          {membershipTicketCount.map((item) => (
            <Item>
              <ItemLabel>
                <LabelDesc>{item.desc}</LabelDesc>
                <LabelTitle>{item.title}</LabelTitle>
              </ItemLabel>
              {label === 'Premium' ? (
                <LeftCount>무제한</LeftCount>
              ) : (
                <Count>
                  <LeftCount>{fmt(item.limit - item.count)}</LeftCount> /{' '}
                  {fmt(item.limit)}건
                </Count>
              )}
            </Item>
          ))}
        </Content>
      )}
    </Container>
  );
}

export default RemainingTicket;

const Container = styled.div`
  display: flex;
  width: 100%;
  padding: 40px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const Title = styled.div`
  align-self: stretch;
  color: #222;
  font-size: 18px;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.36px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Item = styled.div`
  display: flex;
  padding: 16px 0 8px 0;
  align-items: flex-end;
  gap: 16px;
  align-self: stretch;
  border-bottom: 1px solid #e1e2e4;
`;

const ItemLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
`;

const LabelDesc = styled.div`
  color: #616161;
  font-size: 12px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.24px;
`;

const LabelTitle = styled.div`
  color: #222;
  font-size: 15px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.3px;
`;

const Count = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;

  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const LeftCount = styled.div`
  color: #222;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%;
  letter-spacing: -0.32px;
`;

const EmptySection = styled.div`
  display: flex;
  padding: 16px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const EmptyDesc = styled.div`
  align-self: stretch;
  color: rgba(0, 12, 30, 0.8);
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const BuyButton = styled.button`
  border: none;
  outline: none;
  cursor: pointer;

  align-self: stretch;
  display: flex;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #008aff;

  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.32px;
`;
